import '~/lib/zod-config'
import { m } from '@aimfora/i18n'
import { QueryClient } from '@tanstack/react-query'
import { createRouter, Link } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { getGlobalStartContext } from '@tanstack/react-start'
import { reportClientError } from '~/lib/sentry-browser'
import { routeTree } from './routeTree.gen'

function NotFoundPage() {
  return (
    <main
      id="main"
      className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center"
    >
      <p className="font-semibold">{m.common_not_found()}</p>
      <p className="max-w-prose text-ink-soft">{m.common_not_found_note()}</p>
      <Link to="/app" className="underline underline-offset-2">
        {m.common_to_overview()}
      </Link>
    </main>
  )
}

function ErrorPage({ reset }: { reset: () => void }) {
  return (
    <main
      id="main"
      role="alert"
      className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center"
    >
      <p className="font-semibold">{m.common_something_went_wrong()}</p>
      <p className="max-w-prose text-ink-soft">{m.common_error_note()}</p>
      <div className="flex gap-4">
        <button type="button" className="underline underline-offset-2" onClick={reset}>
          {m.common_try_again()}
        </button>
        <Link to="/app" className="underline underline-offset-2">
          {m.common_to_overview()}
        </Link>
      </div>
    </main>
  )
}

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 30_000, retry: false, refetchOnWindowFocus: false } },
  })

  const router = createRouter({
    routeTree,
    context: { queryClient },
    // Scripts only run with this request's nonce (see the security middleware in start.ts)
    ssr: { nonce: getGlobalStartContext()?.nonce },
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: ErrorPage,
    defaultOnCatch: (error) => reportClientError(error),
  })

  setupRouterSsrQueryIntegration({ router, queryClient })
  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
